import { Alert } from "@/components/ui/Alert";
import { Dialog } from "@/components/ui/Dialog";
import { Text } from "@/components/ui/Text";
import { Button } from "@/components/ui/Button";

// ConfirmDialog is the shared confirm-before-acting surface. It stays open and
// uncloseable while the action runs, can show an inline error the user must
// read, and optionally renders an authoritative impact summary (counts +
// affected names) sourced from a server-side pre-flight endpoint so
// destructive actions are never confirmed against client-composed estimates.
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pendingLabel = "Working…",
  onConfirm,
  isPending,
  impact,
  error,
  confirmDisabled = false,
  confirmVariant = "destructive-primary",
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  // Shown on the confirm button while the action runs. Name the action in
  // progress ("Deleting…") rather than leaving the generic default.
  pendingLabel?: string;
  onConfirm: () => void;
  isPending?: boolean;
  impact?: {
    summary: string;
    mcpServerNames?: string[];
    // Heading for the list above. Defaults to the MCP server wording this
    // component was written for; other preflights list other things (the key
    // set delete preflight lists OAuth client_ids).
    namesLabel?: string;
    isLoading?: boolean;
  };
  // Rendered inline above the footer, for refusals the operator has to read and
  // act on rather than dismiss. A toast is the wrong surface for those: it
  // vanishes, and the dialog it describes is still open. Callers that only need
  // "something went wrong" should keep using a toast. Accepts a node so the
  // refusal can carry the link that fixes it.
  error?: React.ReactNode;
  // Blocks the action outright, for a preflight that predicts a refusal rather
  // than merely describing consequences. The impact summary still renders, so
  // the operator can read what is in the way; there is just nothing useful to
  // confirm until they clear it.
  confirmDisabled?: boolean;
  // Confirmations of destructive actions are the common case, so that is the
  // default; a lifecycle step that is safe but still worth a pause (activating
  // a key) reads wrong in red.
  confirmVariant?: "destructive-primary" | "primary";
  // Inputs the confirmation depends on, such as a choice between two ways to
  // perform the action, rendered between the description and the impact.
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isPending) return;
        onOpenChange(nextOpen);
      }}
    >
      <Dialog.Content closeable={!isPending}>
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>
        {children}
        {impact && (
          <div>
            {impact.isLoading ? (
              <Text small muted>
                Checking impact…
              </Text>
            ) : (
              <>
                <Text>{impact.summary}</Text>
                {impact.mcpServerNames && impact.mcpServerNames.length > 0 && (
                  <div className="mt-2">
                    <Text small as="div">
                      {impact.namesLabel ?? "Affected MCP Servers:"}
                    </Text>
                    <ul className="mt-1 list-disc pl-5">
                      {impact.mcpServerNames.map((name, index) => (
                        // Index-keyed: callers pass display labels, and some
                        // (OAuth client_id values) legitimately repeat across
                        // rows. The list is static for the life of the dialog,
                        // so position is a stable identity here.
                        <li key={`${index}-${name}`}>
                          <Text small muted as="span">
                            {name}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {error && (
          <Alert variant="error" dismissible={false}>
            {error}
          </Alert>
        )}
        <Dialog.Footer>
          <Button
            variant="tertiary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <Button.Text>Cancel</Button.Text>
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending || impact?.isLoading || confirmDisabled}
          >
            <Button.Text>{isPending ? pendingLabel : confirmLabel}</Button.Text>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
